import * as echarts from 'echarts/core'
import { BarChart, LineChart, PieChart, ScatterChart } from 'echarts/charts'
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  ToolboxComponent,
  DatasetComponent,
  DataZoomComponent,
  MarkLineComponent,
} from 'echarts/components'
import { LabelLayout, UniversalTransition } from 'echarts/features'
import { CanvasRenderer } from 'echarts/renderers'
import { downloadByUrl } from '@/utils/download'

// 按需注册图表与组件
echarts.use([
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  ToolboxComponent,
  DatasetComponent,
  DataZoomComponent,
  MarkLineComponent,
  LabelLayout,
  UniversalTransition,
  CanvasRenderer,
])

export type ChartInstance = ReturnType<typeof echarts.init>

/** 图表配色（主色 #3F7D58 打头） */
export const chartPalette: string[] = [
  '#3F7D58',
  '#5B8FF9',
  '#F2C94C',
  '#E8684A',
  '#9270CA',
  '#37A2DA',
  '#61DDAA',
  '#F6BD16',
]

let wordCloudReady: boolean | null = null

/** 按需加载词云插件，加载失败时调用方降级为柱状图 */
export async function ensureWordCloud(): Promise<boolean> {
  if (wordCloudReady !== null) return wordCloudReady
  try {
    await import('echarts-wordcloud')
    wordCloudReady = true
  } catch {
    wordCloudReady = false
  }
  return wordCloudReady
}

/** 私有：取 DOM 上的 echarts 实例 */
export function getChartInstance(el: HTMLElement | null | undefined): ChartInstance | null {
  if (!el) return null
  return echarts.getInstanceByDom(el) ?? null
}

/** 导出图表为 PNG */
export function exportChartPng(
  el: HTMLElement | null | undefined,
  filename: string,
  background?: string
): void {
  const instance = getChartInstance(el)
  if (!instance) return
  const url = instance.getDataURL({
    type: 'png',
    pixelRatio: 2,
    backgroundColor: background ?? 'transparent',
  })
  downloadByUrl(url, filename)
}

/** 图表通用基础配置（含主题适配） */
export function baseChartOption(isDark: boolean): Record<string, unknown> {
  const textColor = isDark ? '#E5EAF3' : '#303133'
  const borderColor = isDark ? '#3A3F4B' : '#EBEEF5'
  const splitColor = isDark ? '#2C3038' : '#F2F6FC'
  return {
    color: chartPalette,
    backgroundColor: 'transparent',
    textStyle: { color: textColor, fontFamily: 'Inter, "Microsoft YaHei", sans-serif' },
    grid: { left: 48, right: 24, top: 48, bottom: 36, containLabel: true },
    tooltip: {
      trigger: 'axis',
      backgroundColor: isDark ? '#1F2430' : '#FFFFFF',
      borderColor: borderColor,
      textStyle: { color: textColor },
    },
    legend: {
      textStyle: { color: textColor },
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 12,
    },
    axisCommon: {
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: textColor },
      splitLine: { lineStyle: { color: splitColor } },
    },
  }
}

/** 生成带通用样式的坐标轴配置 */
export function axisOption(isDark: boolean): Record<string, unknown> {
  const base = baseChartOption(isDark)
  return base.axisCommon as Record<string, unknown>
}

function isDarkMode(): boolean {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
}

/**
 * 生成坐标轴类图表（line / bar）的基础配置。
 * 返回松散结构，调用方再覆盖 xAxis.data 与 series。
 */
export function getChartOptions(_type: 'line' | 'bar'): Record<string, any> {
  const dark = isDarkMode()
  const base = baseChartOption(dark)
  const axis = base.axisCommon as Record<string, unknown>
  return {
    color: base.color,
    backgroundColor: base.backgroundColor,
    textStyle: base.textStyle,
    tooltip: base.tooltip,
    legend: { ...(base.legend as Record<string, unknown>), show: true, bottom: 0 },
    grid: { ...(base.grid as Record<string, unknown>), bottom: 48 },
    xAxis: {
      type: 'category',
      data: [],
      axisLine: axis.axisLine,
      axisLabel: axis.axisLabel,
    },
    yAxis: {
      type: 'value',
      axisLine: axis.axisLine,
      axisLabel: axis.axisLabel,
      splitLine: axis.splitLine,
    },
    series: [],
  }
}

/** 生成词云配置（需先调用 ensureWordCloud 动态注册插件） */
export function getWordCloudOptions(data: { name: string; value: number }[]): Record<string, unknown> {
  const dark = isDarkMode()
  return {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'item' },
    textStyle: { color: dark ? '#E5EAF3' : '#303133' },
    series: [
      {
        type: 'wordCloud',
        shape: 'circle',
        width: '100%',
        height: '100%',
        sizeRange: [12, 56],
        rotationRange: [-45, 45],
        gridSize: 8,
        drawOutOfBound: false,
        textStyle: {
          fontFamily: 'Inter, "Microsoft YaHei", sans-serif',
          color: () => chartPalette[Math.floor(Math.random() * chartPalette.length)],
        },
        data: data.map((item) => ({ name: item.name, value: item.value })),
      },
    ],
  }
}

export { echarts }
